<template>
  <Dialog :common-dialog="dialog" :dialog-width="dialogWidth">
    <!-- TITLE -->
    <template v-slot:title>
      <div class="d-flex align-center justify-space-between w-100 px-2 py-1">
        <div class="d-flex align-center">
          <v-avatar size="38" color="cyan lighten-5" class="mr-3">
            <v-icon color="cyan darken-2">mdi-account-search-outline</v-icon>
          </v-avatar>
          <div class="dialog-title color-custom-blue font-weight-700">
            Select a {{ title }}
          </div>
        </div>
        <v-btn
          icon
          small
          class="custom-grey-border bg-slate-50"
          :disabled="loading"
          @click="closeDialog"
        >
          <v-icon color="slate-600" small>mdi-close</v-icon>
        </v-btn>
      </div>
    </template>

    <!-- BODY -->
    <template v-slot:body>
      <div class="px-4 w-100">
        <!-- SEARCH TOOLBAR -->
        <v-card flat outlined class="rounded-lg bg-slate-50 px-3 py-2 mb-3">
          <label
            class="font-size-13 font-weight-600 mb-1 d-block text-slate-700"
          >
            Which {{ title }} would you like to select?
          </label>
          <div class="d-flex align-center">
            <v-text-field
              v-model="search"
              placeholder="Search by name, code or address..."
              dense
              filled
              solo
              flat
              hide-details
              color="cyan"
              prepend-inner-icon="mdi-magnify"
              :loading="loading"
              :disabled="loading"
              class="custom-erp-input bg-white rounded-lg flex-grow-1"
              @keydown.enter="getList"
            />

            <v-btn
              class="ml-2 custom-bold-button"
              color="cyan darken-2 white--text"
              depressed
              height="36"
              :loading="loading"
              :disabled="loading"
              @click="getList"
            >
              <v-icon small left>mdi-magnify</v-icon>
              Search
            </v-btn>
          </div>
        </v-card>

        <!-- LIST AREA -->
        <perfect-scrollbar
          :options="{ suppressScrollX: true }"
          class="scroll pr-1"
          style="max-height: calc(100vh - 340px); position: relative"
        >
          <div v-if="list.length">
            <v-card
              v-for="(row, index) in list"
              :key="index"
              flat
              outlined
              class="rounded-lg px-3 py-2 mb-2 bg-white hover-shadow cursor-pointer transition-fast"
              @click="selectItem(row)"
            >
              <v-row dense align="center">
                <!-- Left: Entity Details -->
                <v-col cols="12" md="8" class="py-1">
                  <div class="d-flex align-center flex-wrap gap-2 mb-1">
                    <Barcode :barcode="row.barcode" />
                  </div>

                  <div
                    class="font-weight-700 font-size-14 color-custom-blue mb-1 d-flex align-center custom-nowrap-ellipsis"
                  >
                    <v-icon small left color="slate-600">mdi-domain</v-icon>
                    <template v-if="row.display_name || row.company_name">
                      {{ row.display_name || row.company_name }}
                    </template>
                    <em v-else class="text-muted font-italic font-weight-400"
                      >no name available</em
                    >
                  </div>

                  <div
                    class="d-flex align-center font-size-12 text-slate-700 custom-nowrap-ellipsis"
                    v-if="
                      row?.single_billing?.street_1 ||
                      row?.single_billing?.unit_no ||
                      row?.single_billing?.street_2
                    "
                  >
                    <v-icon small left color="slate-500"
                      >mdi-map-marker-outline</v-icon
                    >
                    {{ row?.single_billing?.street_1 }}
                    {{ row?.single_billing?.unit_no }}
                    {{ row?.single_billing?.street_2 }}
                  </div>
                </v-col>

                <!-- Right: Select CTA -->
                <v-col
                  cols="12"
                  md="4"
                  class="text-md-right d-flex align-center justify-md-end py-1"
                >
                  <v-btn
                    small
                    depressed
                    color="cyan lighten-5"
                    class="custom-bold-button px-3 cyan--text text-darken-2"
                  >
                    Select {{ title }}
                    <v-icon small right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </div>

          <!-- EMPTY STATE -->
          <div
            v-else
            class="d-flex flex-column align-center justify-center py-10 px-4 text-center border rounded-lg bg-slate-50 border-dashed"
          >
            <v-avatar size="56" color="grey lighten-4" class="mb-3">
              <v-icon color="slate-400" large>mdi-account-off-outline</v-icon>
            </v-avatar>
            <div class="font-weight-700 font-size-15 text-slate-800 mb-1">
              No {{ title }}s Found
            </div>
            <div class="caption-1 text-muted max-w-300">
              There are no matching {{ title.toLowerCase() }} records in the
              database.
            </div>
          </div>
        </perfect-scrollbar>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import { QUERY } from "@/core/services/store/request.module";
import ObjectPath from "object-path";

export default {
  name: "EntitySelectDialog",

  components: {
    Dialog,
    Barcode,
  },

  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "customer", // customer | lead
    },
  },

  data() {
    return {
      search: null,
      list: [],
      loading: false,
    };
  },

  watch: {
    dialog(val) {
      if (val) {
        this.search = null;
        this.getList();
      }
    },
  },

  computed: {
    title() {
      return this.type === "lead" ? "Lead" : "Customer";
    },

    dialogWidth() {
      return Math.floor((window.innerWidth / 100) * 50);
    },
  },

  methods: {
    getList() {
      this.loading = true;
      this.list = [];

      let url = "customer-list";

      if (this.type === "lead") {
        url = "lead-list";
      }

      this.$store
        .dispatch(QUERY, {
          url,
          data: {
            search: this.search,
          },
        })
        .then((response) => {
          this.list = ObjectPath.get(response, "data", []);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    selectItem(row) {
      this.$emit("select", {
        customer: row,
        contact_person: row?.default_person || null,
      });

      this.$emit("close", true);
    },

    closeDialog() {
      this.$emit("close", true);
    },
  },
};
</script>
